import { compileScriptFunction } from '@utils/scriptEval';
import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import { IDictFilter } from '@models/Forms/IForms';

/**
 * Отбор строк справочника, заданный конфигурацией: условие по кодам (`filter`)
 * и пользовательский скрипт (`script`).
 *
 * Формат условия и контракт скрипта — часть конфигураций, а не таблицы: он жил
 * рядом с DX-гридом только исторически. Логика перенесена без изменений.
 */

/**
 * Логическое условие в нотацию справочника.
 *
 * Логический атрибут бэкенд справочника хранит числом («1»/«0»), а серверный
 * источник таблицы шлёт настоящий boolean (иначе списки документов падают на
 * строке «true»). Поэтому здесь повторяем правило `calculateFilterExpression`
 * DX-грида: «да» — `= 1`, «нет» — `<> 1`, чтобы в «нет» попадал и незаполненный
 * флаг. Без этого справочник условие не понимает и отдаёт выборку целиком.
 */
export const dictBooleanFilter = (filter: unknown): unknown => {
    if (!Array.isArray(filter)) return filter;
    const [field, , value] = filter;
    if (typeof field === 'string' && typeof value === 'boolean') return [field, value ? '=' : '<>', 1];
    return filter.map(dictBooleanFilter);
};

/**
 * Предустановленный отбор шаблона (`<filter>`) в фильтр запроса.
 *
 * Условие приходит строкой c кодами: `isShow` задаёт, показываем мы их или
 * прячем, поэтому набор собирается либо через `or` с равенством, либо через
 * `and` с неравенством. Бэкенд принимает и `<>`, и `!=`; парсер предикатов
 * шаблонов понимает только `<>` — используем его.
 */
export const dictPresetFilter = (filterResponse: IDictFilter | undefined): unknown[] | null => {
    if (!filterResponse?.filter) return null;
    const filter = JSON.parse(filterResponse.filter.replace(/'/g, '"'));
    if (!filter.codes) return null;
    const column = filter.columnName ? filter.columnName : 'code';
    const operand = filter.isShow ? '=' : '<>';
    const binary = filter.isShow ? 'or' : 'and';
    const conditions: unknown[] = [];
    (filter.codes as string[]).forEach((code) => {
        if (conditions.length > 0) conditions.push(binary);
        conditions.push([column, operand, code]);
    });
    return conditions.length > 0 ? conditions : null;
};

/** Фильтр таблицы и предустановленный отбор объединяются по «И». */
export const mergeDictFilter = (preset: unknown[] | null, own: unknown): unknown => {
    if (!preset) return own;
    if (own == null) return preset;
    return [preset, 'and', own];
};

/**
 * Проверяет, удовлетворяет ли текущий элемент условиям фильтрации справочника.
 *
 * Учитывает включённые/исключённые значения и columnName.
 * Возвращает true, если элемент должен отображаться.
 *
 * @param {IDictionaryData} currentItem - Элемент справочника.
 * @param {any} filter - Объект фильтра с полями columnName, codes, isShow и exlude.
 * @returns {boolean} Результат фильтрации: true — элемент проходит фильтр.
 */
export const filterPredicate = (currentItem: IDictionaryData, filter: any) => {
    const value = filter.columnName
        ? currentItem.fields.find((x) => x.name === filter.columnName)?.value
        : currentItem.code;

    if (filter.exlude && filter.exlude?.codes?.indexOf(value) !== -1) return false;

    if (filter.codes?.indexOf(value) !== -1) return filter.isShow;

    return !filter.isShow;
};

/**
 * Применяет строковый фильтр из объекта IDictFilter к списку справочников.
 *
 * Фильтр передаётся в виде сериализованного JSON (возможно, с одинарными кавычками),
 * преобразуется обратно в объект, и применяется через `filterPredicate`.
 *
 * @param {IDictionaryData[]} originalItems - Исходный массив элементов справочника.
 * @param {IDictFilter} filter - Объект фильтра, содержащий строку сериализованного фильтра.
 * @returns {IDictionaryData[]} Отфильтрованный массив элементов.
 */
export const filterResponseVoid = (originalItems: IDictionaryData[], filter: IDictFilter) => {
    if (filter.filter && filter.filter != '') {
        const fObj = JSON.parse(filter.filter.replace(/\'/g, '"'));
        const filteredObject = [] as IDictionaryData[];
        for (let j = 0; j < originalItems.length; j++) {
            const item = originalItems[j];
            const filteredChild = filterPredicate(item, fObj);
            if (filteredChild) filteredObject.push(item);
        }

        return filteredObject;
    } else {
        return originalItems;
    }
};

/**
 * Применяет пользовательский скрипт-фильтр ко всем строкам справочника и возвращает отфильтрованные строки.
 *
 * @param {IDictionaryData[]} data - Массив данных справочника.
 * @param {string} script - JavaScript-выражение, которое должно быть выполнено для каждой строки.
 * @returns {IDictionaryData[]} Только те строки, для которых скрипт вернул true.
 */
export const filterGridRowsByScript = (data: IDictionaryData[], script: string) => {
    const _data: IDictionaryData[] = [];
    data.forEach((value) => {
        const result = filterGridRowByScriptEval(value, data, script);
        if (result) {
            _data.push(value);
        }
    });
    return _data;
};

/**
 * Проверяет, есть ли в справочнике хотя бы одна строка, удовлетворяющая скрипту.
 *
 * @param {IDictionaryData[]} data - Массив данных справочника.
 * @param {string} script - JavaScript-выражение для проверки.
 * @returns {boolean} False, если хотя бы одна строка прошла фильтр (то есть нужна проверка), иначе true.
 */
export const checkGridRowsByScript = (data: IDictionaryData[], script: string) => {
    for (let index = 0; index < data.length; index++) {
        const value = data[index];
        const result = filterGridRowByScriptEval(value, data, script);
        if (result) {
            return false;
        }
    }
    return true;
};

/**
 * Выполняет JavaScript-выражение для одной строки справочника и возвращает результат.
 * Скрипт имеет доступ к вспомогательным функциям: `field`, `code`, `columnValues`, `intersect`, `getDateInt`, и др.
 *
 * ⚠ ВНИМАНИЕ: Скрипт исполняется как код. Он должен быть проверен и безопасен!
 *
 * @param {IDictionaryData} data - Текущая строка справочника.
 * @param {IDictionaryData[]} dataArray - Все строки (для получения агрегатных значений, если нужно).
 * @param {string} script - Скрипт, возвращающий логическое значение.
 * @returns {boolean} Результат выполнения скрипта.
 */
export const filterGridRowByScriptEval = (data: IDictionaryData, dataArray: IDictionaryData[], script: string) => {
    /**
     * WARNING! Begin sections of functions for templates, do not rename
     * */
    const subFunc = ` 
    function code (){
        return data.code;
    };

    function getDateInt (data) {
        // dd.MM.yyyy → unix ms. Парсим явно: stringToDateFormat был внешней зависимостью,
        // которая в этой eval-области никогда не была определена (helpersDatePicker удалён).
        var head = (data || '').split(' ')[0];
        var parts = head.split('.');
        if (parts.length < 3) return NaN;
        var dd = parseInt(parts[0], 10);
        var mm = parseInt(parts[1], 10) - 1;
        var yyyy = parseInt(parts[2], 10);
        return new Date(yyyy, mm, dd).getTime();
    };

    function getOnlyDateFromInt (data) {
        let date = new Date(+data);
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0).getTime();
    };

    function field (name) {
        let value = data.fields.find((field) => field.name === name)?.value;
        return value;
    };

    function columnValues (name) {
        let arr = [];
        dataArray.forEach((row)=>{
            let value = row.fields.find((field) => field.name === name)?.value;
            arr.push(value);
        });
        return arr;
    };
    function eliminateDuplicates(arr) {
        let i;
         let   len = arr.length;
         let   out = [];
         let  obj = {};
      
        for (i = 0; i < len; i++) {
          obj[arr[i]] = 0;
        }
        for (i in obj) {
          out.push(i);
        }
        return out;
      };

    function intersect (array1, array2){
        return array1 && array2 && array1.some((item) => array2.includes(item));
    };
`;

    // Скрипт из конфигурации — всегда выражение; компиляция с явными параметрами
    // вместо прямого eval, чтобы не тянуть локальный скоуп модуля.
    const expression = script.trim().replace(/;+\s*$/, '');
    return compileScriptFunction(['data', 'dataArray'], `${subFunc}\nreturn (${expression});`)(data, dataArray);
};
